import {GetStaticPropsContext, InferGetStaticPropsType} from 'next';
import Head from 'next/head';
import {ParsedUrlQuery} from 'querystring';

import Col from '../../features/common/Col';
import Row from '../../features/common/Row';
import {getCmsEntry, getCmsList} from '../../features/strapi';
import {ApiFooterFooter, ApiResourceResource} from '../../features/strapi/generated/contentTypes';
import Footer from '../../features/tool/components/Footer';
import {getToolLayout} from '../../features/tool/components/Layout';
import ResourceCard from '../../features/tool/components/ResourceCard';
import ResourcePageContent from '../../features/tool/components/ResourcePageContent';
import {buildStaticPropsResponse} from '../../features/tool/util/dataFetch';
import {generateTOC, TOC} from '../../features/tool/util/generateTOC';
import {NextPageWithLayout} from '../_app';

type ResourceParams = ParsedUrlQuery & {
  slug: string;
};
const ResourcePage: NextPageWithLayout<InferGetStaticPropsType<typeof getPageData>> = ({
  resource,
  toc,
  resources,
  footer,
}) => {
  return (
    <>
      <Head>
        <title>{resource.title} - Envirocrops</title>
      </Head>

      <ResourcePageContent resource={resource} toc={toc ?? undefined} />

      <Col className="bg-primary-100 px-48 mobile:px-12 space-y-12 pt-24 pb-32">
        <div className="text-3xl">Recommended For You</div>
        <Row className="flex-nowrap mobile:overflow-x-scroll gap-md mobile:-mx-12 mobile:px-12">
          {resources.map((rc, i) => (
            <ResourceCard
              key={`resources_card_${i}`}
              className="flex-1 mobile:flex-none mobile:w-10/12 mobile:flex-shrink-0"
              data={rc}
            />
          ))}
        </Row>
      </Col>
      <Footer data={footer} />
    </>
  );
};

export const getStaticPaths = async () => ({paths: [], fallback: 'blocking'});

const getPageData = async (context: GetStaticPropsContext) => {
  const {slug} = context.params as ResourceParams;
  return buildStaticPropsResponse({
    // Current resource
    ...(await getCmsEntry<ApiResourceResource, 'resource'>({
      path: 'resources',
      params: {populate: ['image', 'author', 'category'], filters: {slug: {$eq: slug}}},
      rootKey: 'resource',
      processDataMap: {
        image: 'image',
        author: 'commonChild',
        category: 'commonChild',
        body: 'markdown',
        date: 'date',
        html_body: 'html',
      },
    })),
    // Related resources
    ...(await getCmsList<ApiResourceResource, 'resources'>({
      path: 'resources',
      params: {populate: ['category', 'image', 'author'], pagination: {page: 1, pageSize: 3}, sort: ['id:desc']},
      processDataMap: {image: 'image', category: 'commonChild', author: 'commonChild', date: 'date'},
    })),
    ...(await getCmsEntry<ApiFooterFooter, 'footer'>({path: 'footer'})),
    ...{toc: null as TOC | null},
  });
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const result = await getPageData(context);
  if (!result.props.resource) return {notFound: true};

  const {toc, html_body} = generateTOC(result.props.resource.html_body);
  if (toc) result.props.toc = toc;
  if (html_body) result.props.resource.html_body = html_body;

  return result;
};

export default ResourcePage;

ResourcePage.getLayout = getToolLayout;

ResourcePage.title = 'Resource';
